import {Injectable} from '@angular/core';
import {FormGroup} from '@angular/forms';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {ActionSheetController, LoadingController, Platform, ToastController} from '@ionic/angular';
// @ts-ignore
import moment from 'moment';
import {ToastButton} from '@ionic/core/dist/types/components/toast/toast-interface';
import {AppStateService} from '../providers/app-state.service';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Doctor, VisitType} from "../models/vet-clinic-data.model";
import {ChatMessage} from "../models/chat-message.model";
import {UserAuthData} from "../models/user-auth-data.model";
import {DataGridSearchField} from "../models/data-grid-search-field.model";
import {VetClinic, VImage} from "../models/vet-clinic.model";
import {Network} from "@capacitor/network";
import {Storage} from "@ionic/storage-angular";
import {TranslateService} from "@ngx-translate/core";
import {DataGridEnumColumnItem} from "../models/data-grid-enum-column-item.model";

@Injectable({
  providedIn: 'root'
})
export class CommonUtils {
  public infoToast: any;
  private tags: string[] = ['KlinikaXP', 'Aplikacja Vetineo', 'Dashboard Vetineo', 'Terminarz KlinikiXP',
    'Rezerwacja Internetowa', 'Strona WWW', 'Poczekalnia', 'Typy Wizyt', 'Lekarze', 'Ustawienia', 'Wyniki Badań', 'Profilaktyka', 'Vouchery', 'Prawne', 'Rozliczenia', 'Powiadomienia', 'Mail'];

  constructor(
    private router: Router,
    private toastController: ToastController,
    private loadingController: LoadingController,
    private platform: Platform,
    private actionSheetController: ActionSheetController,
    private appStateService: AppStateService,
    private http: HttpClient,
    private storage: Storage,
    private translate: TranslateService
  ) {
  }

  getTags() {
    return this.tags;
  }

  isFunction(functionToCheck) {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
  }

  debounce(reconnectFrequencySeconds, func, wait) {
    let timeout;
    let waitFunc;

    const that = this;

    return function () {
      if (that.isFunction(wait)) {
        waitFunc = wait;
      } else {
        waitFunc = () => wait;
      }

      const context = this;
      const args = arguments;
      const later = () => {
        timeout = null;
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, waitFunc(reconnectFrequencySeconds));
    };
  }

  delayedExec(func, timeout?: number): void {
    if (func && timeout) {
      const that = this;
      if (that.isFunction(func)) {
        setTimeout(func, timeout);
      }
    }
  }

  getFakeRequestDelay() {
    return this.getEnvironmentConfigProperty('FAKE_REQUEST_DELAY');
  }

  getEnvironmentConfigProperty(propertyName) {
    return environment[propertyName];
  }

  bestCopyEver(src) {
    return Object.assign({}, src);
  }

  bestTypedCopyEver<T>(target: T, src) {
    return Object.assign(target, src);
  }

  bestTypedCopyEverFromMap<T>(target: T, srcMap: Map<string, any>) {
    const obj = Array.from(srcMap.entries()).reduce((main, [key, value]) => ({...main, [key]: value}), {});
    return Object.assign(target, obj);
  }

  bestTypedArrayCopyEver<TT, T>(elementObj: TT, target: T, src: Array<any>) {
    if (Array.isArray(target) && Array.isArray(src)) {
      src.forEach((arrElement) => {
        elementObj = {} as TT;
        target.push(this.bestTypedCopyEver<TT>(elementObj, arrElement));
      });
    }
    return target;
  }

  formGroupValuesToObject<T>(target: T, formGroup: FormGroup) {
    if (formGroup && formGroup.valid) {
      return Object.assign(target, formGroup.value);
    } else {
      return null;
    }
  }

  formGroupValuesInputedToObject<T>(target: T, formGroup: FormGroup) {
    if (formGroup) {
      return Object.assign(target, formGroup.value);
    } else {
      return null;
    }
  }


  dateToMinutes(date) {
    if (this.isNotEmpty(date)) {
      return date.getHours() * 60 + date.getMinutes();
    }
  }

  dateTime2Str(date) {
    if (this.isNotEmpty(date)) {
      const year = date.getYear() + 1900;
      const month = date.getMonth() + 1;
      const day = date.getDate();

      const hour = date.getHours();
      const minute = date.getMinutes();
      const seconds = date.getSeconds();

      return year + '-' + this.fill0(month) + '-' + this.fill0(day) + ' ' + this.fill0(hour) + ':' + this.fill0(minute) + ':' + this.fill0(seconds);
    }

    return null;
  }

  hhmmToMinutes(minutesHHMM) {
    const t = minutesHHMM.split(':');

    return (60 * parseInt(t[0], 2)) + (parseInt(t[1], 2));
  }

  isFormFieldInvalid(form: FormGroup, fieldName: string, validationType: string) {
    return form.get(fieldName)?.hasError(validationType) && form.get(fieldName)?.touched;
  }

  isFormInvalid(form: FormGroup, validationType: string) {
    return form.errors && form.errors[validationType];
  }

  getStorageObject<T>(key: string): Promise<T> {
    return this.storage.get(key).then(ret => {
      return JSON.parse(JSON.stringify(ret));
    });
  }

  setStorageObject(key: string, value: any): Promise<any> {
    return this.storage.set(key, value);
  }

  removeStorageObject(key: string): Promise<any> {
    return this.storage.remove(key);
  }

  getLocalStorageObject(key: string): any {
    if (window.localStorage !== undefined) {
      const itemJson = window.localStorage.getItem(key);
      if (this.isNotEmpty(itemJson)) {
        try {
          return JSON.parse(itemJson);
        } catch (err) {
        }
      }
    }

    return null;
  }

  setLocalStorageObject(key: string, obj: any): void {
    if (window.localStorage !== undefined && this.isNotEmpty(obj)) {
      try {
        window.localStorage.setItem(key, JSON.stringify(obj));
      } catch (err) {
      }
    }

    return null;
  }

  removeLocalStorageObject(key: string): void {
    if (window.localStorage !== undefined) {
      try {
        window.localStorage.removeItem(key);
      } catch (err) {
      }
    }

    return null;
  }

  generateUUID() {
    let d = new Date().getTime();
    // tslint:disable-next-line:only-arrow-functions
    const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      // tslint:disable-next-line:no-bitwise
      const r = (d + Math.random() * 16) % 16 | 0;
      d = Math.floor(d / 16);
      // tslint:disable-next-line:no-bitwise
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
    return uuid;
  }

  isNotEmpty(obj) {
    return !this.isEmpty(obj);
  }

  isEmpty(obj) {
    if (obj == null) {
      return true;
    }

    if (!isNaN(obj)) {
      if (obj === 0) {
        return true;
      } else {
        return false;
      }
    }

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        return false;
      }
    }

    return true;
  }

  simpleDateFromString(str: string) {
    return str?.split(' ')[0]
  }

  fill0(numberValue) {
    if (!isNaN(numberValue)) {
      return (numberValue < 10 ? '0' + numberValue : numberValue);
    }
  }

  dateFromString(str: string) {
    const a = str.split(/[^0-9]/).map(s => parseInt(s, 10));
    return new Date(a[0], a[1] - 1 || 0, a[2] || 1, a[3] || 0, a[4] || 0, a[5] || 0, a[6] || 0);
  }

  date2Str(date) {
    if (this.isNotEmpty(date)) {
      const year = date.getYear() + 1900;
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return year + '-' + this.fill0(month) + '-' + this.fill0(day);
    }

    return null;
  }

  date2StrWithDots(date) {
    if (this.isNotEmpty(date)) {
      const year = date.getYear() + 1900;
      const month = date.getMonth() + 1;
      const day = date.getDate();

      return this.fill0(day) + '.' + this.fill0(month) + '.' + year;
    }

    return null;
  }

  date2StrLong(date) {
    if (this.isNotEmpty(date)) {
      const year = date.getYear() + 1900;
      const month = date.getMonth() + 1;
      const day = date.getDate();

      const hour = date.getHours();
      const minute = date.getMinutes();

      return `${year}-${this.fill0(month)}-${this.fill0(day)} ${this.fill0(hour)}:${this.fill0(minute)}`;
    }

    return null;
  }

  gerCurrentDateFormatted(format) {
    return moment().format(format);
  }

  findInObjectsArray(arr, key: string, value: string) {
    if (this.isEmpty(arr) || this.isEmpty(key) || this.isEmpty(value)) {
      return -1;
    }
    for (let i = 0; i < arr.length; i++) {
      // console.log(arr[i][key] + ' : ' + value)
      if (arr[i][key] === value) {
        return i;
      }
    }
    return -1;
  }

  findObjectsArray(arr, key: string, value: string) {
    if (this.isEmpty(arr) || this.isEmpty(key) || this.isEmpty(value)) {
      return -1;
    }
    let idx = 0;
    for (const el of arr) {
      if (arr[idx][el] === value) {
        return arr[idx];
      }
      idx++;
    }
    return undefined;
  }

  goTo(page: string) {
    this.router.navigateByUrl(page);
  }

  goToCmd(commands: string[], callback?) {
    this.router.navigate(commands).then(() => {
      if (callback) {
        callback();
      }
    });
  }

  isNotOnWifi(): Promise<boolean> {
    return this.platform.ready().then(() => {
      return Network.getStatus().then(status => {
        console.log(status);
        if (status && status.connectionType !== 'wifi') {
          return true;
        } else {
          console.log('wifi');
          return false;
        }
      }, error => {
        return true;
      });
    }, error => {
      return true;
    });
  }

  isOnLine(): Promise<boolean> {
    return this.platform.ready().then(() => {
      return Network.getStatus().then(status => {
        console.log(status);
        if (status && status.connected) {
          return true;
        } else {
          return false;
        }
      }, error => {
        return false;
      });
    }, error => {
      return false;
    });
  }

  async presentInfoToastIfNotExistent(message: string) {
    if (this.infoToast) {
      this.infoToast = await this.toastController.create({
        header: message,
        position: 'bottom',
        buttons: [{
          text: 'Ok',
          role: 'cancel',
          handler: () => {
          }
        }
        ]
      });
      const that = this;

      this.infoToast.present();
    }
  }

  async presentInfoToast(message: string, alertPosition: 'top' | 'bottom' | 'middle' = 'bottom', duration: number = 0) {
    const toast = await this.toastController.create({
      header: message,
      position: alertPosition,
      duration: (duration > 0 ? duration : 4000),
      buttons: [{
        text: 'Ok',
        role: 'cancel',
        handler: () => {
        }
      }
      ]
    });
    toast.present();
  }

  async presentInfoToastWithoutDurationLimit(message: string, alertPosition: 'top' | 'bottom' | 'middle' = 'bottom') {
    const toast = await this.toastController.create({
      header: message,
      position: alertPosition,
      buttons: [{
        text: 'Ok',
        role: 'cancel',
        handler: () => {
        }
      }
      ]
    });
    toast.present();
  }

  async presentSimpleInfoToast(message: string, alertPosition: 'top' | 'bottom' | 'middle' = 'bottom', duration: number = 0) {
    const toast = await this.toastController.create({
      header: message,
      position: alertPosition,
      duration: (duration > 0 ? duration : 4000)
    });
    toast.present();
  }

  async presentYesNoActionSheet(title: string, yesBtnTitle: string, noBtnTitle: string, yesIconTitle: string, noIconTitle: string, yesHandler, noHandler, addCancelBtn = false, subHeader: string = null) {
    const actionSheetOptions = {
      header: title,
      subHeader: undefined,
      buttons: [{
        text: yesBtnTitle,
        role: 'destructive',
        icon: yesIconTitle,
        handler: yesHandler
      }, {
        text: noBtnTitle,
        role: 'destructive',
        icon: noIconTitle,
        handler: noHandler
      }]
    };

    if (subHeader) {
      actionSheetOptions.subHeader = subHeader;
    }

    if (addCancelBtn) {
      actionSheetOptions.buttons.push({
        text: 'Anuluj',
        icon: 'close',
        role: 'cancel',
        handler: () => {
          console.log('Cancel clicked');
        }
      });
    }

    const actionSheet = await this.actionSheetController.create(actionSheetOptions);
    await actionSheet.present();
  }

  async presentComplexInfoToast(message: string, toastPosition: 'bottom' | 'middle' | 'top', toastButtons: ToastButton[]) {
    const toast = await this.toastController.create({
      header: message,
      position: toastPosition,
      buttons: toastButtons
    });
    toast.present();
  }

  async dismissToast() {
    await this.toastController.dismiss();
  }

  async presentAlertToast(message: string, duration: number = 0) {
    const toast = await this.toastController.create({
      header: message,
      position: 'bottom',
      color: 'danger',
      duration: (duration > 0 ? duration : 4000),
      buttons: [{
        text: 'Zamknij',
        role: 'cancel',
        handler: () => {
        }
      }
      ]
    });
    toast.present();
  }

  async presentLoading(durationTime: number = 0): Promise<HTMLIonLoadingElement> {
    const loading = await this.loadingController.create({
      spinner: 'circular',
      translucent: true,
      backdropDismiss: false,
      message: 'Proszę czekać',
      duration: durationTime
    });
    await loading.present();

    return loading;
  }

  async dismissLoading() {
    this.loadingController.dismiss();
  }

  public storeMessages(visitUUID: string, type: 'received' | 'sent' | string, messages: ChatMessage[]) {
    if (messages?.length > 0) {
      this.setStorageObject(`${visitUUID}-visit-${type}-messages`, messages).then(() => {
        // ok
      });
    }
  }

  async updateApp() {
    const loadingInfo: HTMLIonLoadingElement = await this.presentLoading();
    this.dismissLoading();
  }

  async performManualUpdate() {
    const isNotOnWifi = this.isNotOnWifi();
    let question = '';
    if (isNotOnWifi) {
      question = `Brak połączenia wifi. Pobranie aktualizacji przy pomocy łącza komórkowego może powodować dodatkowe opłaty.`;
    }

    this.presentYesNoActionSheet('Czy chcesz zaktualizować aplikację?', 'Tak', 'Nie', 'cloud-download-outline', 'close', () => {
      this.updateApp();
    }, undefined, false, question);
  }

  // Booking

  paymentForVisitRequired(visitType: VisitType): boolean {
    if (visitType && (visitType.prepaidRequired && visitType.prepaidPrice)) {
      return true;
    }
    return false;
  }

  hasPrepaidRequired(doctor: Doctor): boolean {
    return doctor && doctor.prepaidRequired && doctor.prepaidVisitPrice !== null;
  }

  handleHttpResponseShowInfoToast(response: any, successInfoText: string, errorInfoText: string): boolean {
    if (response) {
      if (response instanceof HttpErrorResponse) {
        if (errorInfoText) {
          this.presentInfoToast(errorInfoText);
        }
        return false;
      } else {
        if (successInfoText) {
          this.presentInfoToast(successInfoText);
        }
        return true;
      }
    } else {
      return false;
    }
  }

  userHasRole(requiredRole: string, userAuthData: UserAuthData) {
    if (requiredRole && userAuthData && userAuthData.roles) {
      if (userAuthData.roles.find(role => role === requiredRole)) {
        return true;
      }
    }

    return false;
  }

  counter(i: number) {
    return new Array(i);
  }

  mapToSearchFieldList(input: Map<string, string>) {
    if (input) {
      let out = [];
      input.forEach((v, k) => {
        out.push(new DataGridSearchField(k, v));
      })
      return out;
    }
  }

  getVImageUrlPath(vetClinicUUID: string, width: '640' | '1024' | '1440' | '1920', vImage: VImage, fallbackPath: string = 'svg', fallbackFileName: string = 'avatar.svg'): string {
    if (vImage && vetClinicUUID) {
      if (vImage.versions && vImage.versions.includes(width)) {
        return `${environment.CDN_IMAGES_PATH}/${vetClinicUUID}/images/${width}/${vImage.path}`
      } else {
        return `${environment.CDN_IMAGES_PATH}/${vetClinicUUID}/images/src/${vImage.path}`
      }
    } else {
      return this.getPublicAssetUrl(fallbackPath, fallbackFileName);
    }

    return null;
  }

  getCloudVImageUrlPath(imageFolder: string, vClientUUID: string, width: '640' | '1024' | '1440' | '1920', vImage: VImage, fallbackPath: string = 'svg', fallbackFileName: string = 'avatar.svg'): string {
    if (vImage && vClientUUID) {
      if (vImage.versions && vImage.versions.includes(width)) {
        return `${environment.CLOUD_CDN_IMAGES_PATH}/${vClientUUID}/images/${imageFolder}/${vImage.currentVersionFolder}/${width}/${vImage.path}`
      } else {
        return `${environment.CLOUD_CDN_IMAGES_PATH}/${vClientUUID}/images/${imageFolder}/${vImage.currentVersionFolder}/src/${vImage.path}`
      }
    }

    return null;
  }

  getPublicAssetUrl(path: string, fileName: string): string {
    if (path && fileName) {
      return `${environment.LOCAL_PUBLIC_ASSETS_PATH}/${path}/${fileName}`
    }

    return '';
  }

  immutableArrayMove(arr, from, to) {
    return arr.reduce((prev, current, idx, self) => {
      if (from === to) {
        prev.push(current);
      }
      if (idx === from) {
        return prev;
      }
      if (from < to) {
        prev.push(current);
      }
      if (idx === to) {
        prev.push(self[from]);
      }
      if (from > to) {
        prev.push(current);
      }
      return prev;
    }, []);
  }

  getModelFieldNameTKey(modelName: string, fieldName: string) {
    if (modelName && fieldName) return `models.${modelName}.${fieldName}`;
  }


  getLeadStatusLabel(status: string): string {
    return this.translate.instant(`LeadStatus.${status}`)
  }

  getVoivodeshipLabel(voivodeship: string): string {
    return this.translate.instant(`Voivodeship.${voivodeship}`)
  }


  getOrderStatusLabel(status: number): string {
    return this.translate.instant(`OrderStatus.${status?.toString()}`)
  }

  getSettlementStatusLabel(status: string) {
    if (status === '-') {
      return '-';
    } else {
      return this.translate.instant(`SettlementStatus.${status}`)
    }
  }

  getAvailibilityLabel(status: string) {
    if (status === '-') {
      return '-';
    } else {
      return this.translate.instant(`Availability.${status}`)
    }
  }

  getVMessageType(type: string) {
    return this.translate.instant(`VMessageType.${type}`)
  }

  getOfferTypeLabel(offerType: string) {
    return this.translate.instant(`OfferType.${offerType}`)
  }

  getTransactionDispatchStatus(status: string) {
    return this.translate.instant(`TransactionDispatchStatus.${status}`)
  }

  translateOrder(field: string) {
    return this.translate.instant(`models.Order.${field}`)
  }

  translateReservation(field: string) {
    return this.translate.instant(`models.VetReservation.${field}`)
  }


  getRefundTypeEnumValue(type: string) {
    return this.translate.instant(`RefundType.${type}`)
  }

  getRefundStatusEnumValue(status: string) {
    return this.translate.instant(`RefundStatus.${status}`)
  }

  shallowEqual(object1, object2) {
    if (object1 && object2 && object1 instanceof Map && object2 instanceof Map) {
      const map1 = new Map(object1);
      const map2 = new Map(object2);

      if (map1.size !== map2.size) {
        return false;
      }

      for (let tkey of object1.keys()) {
        let v1 = map1.get(tkey);
        let v2 = map2.get(tkey);

        if (v1 !== v2) {
          return false;
        }
      }
    } else {
      const keys1 = Object.keys(object1);
      const keys2 = Object.keys(object2);
      if (keys1.length !== keys2.length) {
        return false;
      }

      for (let key of keys1) {
        if (object1[key] instanceof Map && object2[key] instanceof Map) {
          const map1 = new Map(object1[key]);
          const map2 = new Map(object2[key]);
          for (let tkey of map1.keys()) {
            let v1 = map1.get(tkey);
            let v2 = map2.get(tkey);

            if (v1 !== v2) {
              return false;
            }
          }
        } else {
          if (object1[key] !== object2[key]) {
            return false;
          }
        }
      }
    }

    return true;
  }

  dateToYearMonthDayString(date: Date) {

    const year = date.getFullYear().toString();
    const month = date.getMonth() + 1;
    let monthString = month.toString();
    const day = date.getDate();
    let dayString = day.toString();
    if (month < 10) {
      monthString = '0'.concat(monthString);
    }
    if (day < 10) {
      dayString = '0'.concat(dayString);
    }
    return year.concat('-', monthString, '-', dayString);
  }

  getReservationTypeLabel(reservationType: string) {
    return this.translate.instant(`ReservationType.${reservationType}`)
  }

  getUserRegistrationSourceLabel(userRegistrationSource: string) {
    return this.translate.instant(`UserRegistrationSource.${userRegistrationSource}`)
  }

  getTranslatedScheduledEmailEventLabel(type: string) {
    return this.translate.instant(`ScheduledEmailEventType.${type}`)
  }

  getBuyerTypeLabel(buyerType: string) {
    return this.translate.instant(`BuyerType.${buyerType}`)
  }

  getVisitTypeCategoryLabelTypeLabel(categoryCode: string, categories: DataGridEnumColumnItem[]) {

    return categories.find(category => category.value === categoryCode)?.label;
  }

  translateVetinoVisitTypeCategory(category: string) {
    return this.translate.instant(`VetineoVisitTypeCategory.${category}`);
  }

   transliterate(text: string): string {
    const polishToAsciiMap: { [key: string]: string } = {
      'ą': 'a', 'ć': 'c', 'ę': 'e', 'ł': 'l', 'ń': 'n', 'ó': 'o', 'ś': 's', 'ź': 'z', 'ż': 'z',
      'Ą': 'A', 'Ć': 'C', 'Ę': 'E', 'Ł': 'L', 'Ń': 'N', 'Ó': 'O', 'Ś': 'S', 'Ź': 'Z', 'Ż': 'Z'
    };

    const transliteratedText = text
      .split('')
      .map(char => polishToAsciiMap[char] || char)
      .join('');


    const urlFriendlyText = transliteratedText
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-');

    return urlFriendlyText;
  }

  translateVetReservationConfirmationStatus(confirmationStatus: string) {
    if (confirmationStatus && confirmationStatus === 'CONFIRMED') {
      return 'rezerwacja potwierdzona';
    } else {
      return 'brak potwierdzenia';
    }
  }

  translateReservationType(reservationType: string) {
    return this.translate.instant(`ReservationType.${reservationType}`)
  }

  translateCancelationType(cancelationType: string) {
    return this.translate.instant(`VetReservationCancelationType.${cancelationType}`)
  }

  arrayToCsv(array2d: any[][]){
    return array2d.map(row =>
      row
        .map(String)  // convert every value to String
        .map(v => v.replaceAll('"', '""'))  // escape double quotes
        .map(v => `"${v}"`)  // quote it
        .join(',')  // comma-separated
    ).join('\r\n');  // rows starting on new lines
  }

  downloadBlob(content: any, filename: string, contentType: string) {
    const bom = '\uFEFF';
    const finalContent = bom + content;
    const blob = new Blob([finalContent], { type: contentType });
    const url = URL.createObjectURL(blob);

    const anchorElement = document.createElement('a');
    anchorElement.href = url;
    anchorElement.setAttribute('download', filename);
    anchorElement.click();
    anchorElement.remove();
  }

  sanitizeFileName(name: string) {
    return name.replace(/[\\\/:*?"<>|]/g, '_'); // zamienia niedozwolone znaki na podkreślenia
  }

  getClinicNameWithAddress(clinic:VetClinic) {
    return `${clinic.clinicName} / ${clinic.city}, ${clinic.clinicAddress1}`;
  }

  getVetineoAccountStatusLabel(status: string) {
    return this.translate.instant(`VetineoAccountStatus.${status}`)
  }

  getGovStatusLabel(status: string) {
    if (!status || status == '-') {
      return '-';
    }
    return this.translate.instant(`KSeFGovStatus.${status}`)
  }
}


